/**
 * Validate Usage Tool
 *
 * Validates if component usage follows design system guidelines.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import componentCatalog from '../data/component-catalog.json' with { type: 'json' };
import type { ComponentCatalog } from '../types.js';

const catalog = componentCatalog as ComponentCatalog;

const ValidateUsageSchema = z.object({
  componentName: z.string().describe('Name of the component being used'),
  props: z.record(z.unknown()).describe('Props being passed to the component'),
  context: z
    .string()
    .optional()
    .describe('Where the component is being used (e.g., "inside a form", "in modal")'),
});

interface ValidationIssue {
  type: 'error' | 'warning' | 'suggestion';
  message: string;
  prop?: string;
}

/**
 * Register the validate_usage tool
 */
export function registerValidateUsageTool(server: McpServer): void {
  server.tool(
    'validate_component_usage',
    'Validate if component usage follows design system guidelines',
    ValidateUsageSchema.shape,
    async (params) => {
      const { componentName, props, context } = ValidateUsageSchema.parse(params);

      const component = catalog.components.find(
        (c) => c.name.toLowerCase() === componentName.toLowerCase()
      );

      if (!component) {
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify(
                {
                  valid: false,
                  errors: [{ type: 'error', message: `Unknown component "${componentName}"` }],
                },
                null,
                2
              ),
            },
          ],
        };
      }

      const issues: ValidationIssue[] = [];

      // Check for unknown props
      const knownProps = new Set(component.props.map((p) => p.name));
      for (const propName of Object.keys(props)) {
        if (!knownProps.has(propName) && propName !== 'class' && propName !== 'children') {
          issues.push({
            type: 'warning',
            message: `Unknown prop "${propName}" - not in component API`,
            prop: propName,
          });
        }
      }

      // Check for required props
      for (const propDef of component.props) {
        if (propDef.required && !(propDef.name in props)) {
          issues.push({
            type: 'error',
            message: `Missing required prop "${propDef.name}"`,
            prop: propDef.name,
          });
        }
      }

      // Check variant values
      for (const variant of component.variants) {
        const propValue = props[variant.name];
        if (propValue !== undefined && !variant.values.includes(String(propValue))) {
          issues.push({
            type: 'error',
            message: `Invalid value "${propValue}" for "${variant.name}". Valid values: ${variant.values.join(', ')}`,
            prop: variant.name,
          });
        }
      }

      // Context-specific suggestions
      if (context) {
        if (context.includes('form') && component.name === 'Button') {
          const buttonType = props['type'];
          if (!buttonType) {
            issues.push({
              type: 'suggestion',
              message: 'Consider setting type="submit" for form submission buttons',
              prop: 'type',
            });
          }
        }

        if (context.includes('modal') || context.includes('dialog')) {
          if (component.category === 'layout') {
            issues.push({
              type: 'warning',
              message: `Layout component "${component.name}" is typically not used inside modals`,
            });
          }
        }
      }

      // Accessibility checks
      if (component.name === 'Button' && props['size'] === 'icon' && !props['aria-label']) {
        issues.push({
          type: 'error',
          message: 'Icon-only buttons must have an aria-label for accessibility',
          prop: 'aria-label',
        });
      }

      const errors = issues.filter((i) => i.type === 'error');
      const warnings = issues.filter((i) => i.type === 'warning');
      const suggestions = issues.filter((i) => i.type === 'suggestion');

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                valid: errors.length === 0,
                component: component.name,
                errors,
                warnings,
                suggestions,
                propCount: Object.keys(props).length,
                availableProps: component.props.map((p) => p.name),
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );
}
